/** Phosphor fill paths. A canvas cannot render the React icons, and a hairline stroke washes out at 11px. */

const ICON_VIEWBOX = 256;

export const CANVAS_ICON = {
  funnel:
    "M227.81,66.76l-.08.09L160,139.17v55.49A16,16,0,0,1,152.87,208l-32,21.34A16,16,0,0,1,96,216V139.17L28.27,66.85l-.08-.09A16,16,0,0,1,40,40H216a16,16,0,0,1,11.84,26.76Z",
  barsArrowDown:
    "M208,32H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM72,72h40a8,8,0,0,1,0,16H72a8,8,0,0,1,0-16Zm0,48h48a8,8,0,0,1,0,16H72a8,8,0,0,1,0-16Zm96,64H72a8,8,0,0,1,0-16h96a8,8,0,0,1,0,16Zm29.66-82.34a8,8,0,0,1-11.32,0L176,91.31V136a8,8,0,0,1-16,0V91.31l-10.34,10.35a8,8,0,0,1-11.32-11.32l24-24a8,8,0,0,1,11.32,0l24,24A8,8,0,0,1,197.66,101.66Z",
  barsArrowUp:
    "M208,32H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM72,72h96a8,8,0,0,1,0,16H72a8,8,0,0,1,0-16Zm40,112H72a8,8,0,0,1,0-16h40a8,8,0,0,1,0,16Zm8-48H72a8,8,0,0,1,0-16h48a8,8,0,0,1,0,16Zm77.66,29.66-24,24a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L160,164.69V120a8,8,0,0,1,16,0v44.69l10.34-10.35a8,8,0,0,1,11.32,11.32Z",
} as const;

export type CanvasIconName = keyof typeof CANVAS_ICON;

// Path2D construction is not free and these are redrawn on every grid paint, so
// each icon is parsed once for the life of the tab.
const pathCache = new Map<string, Path2D>();

const iconPath = (d: string): Path2D => {
  const cached = pathCache.get(d);
  if (cached) return cached;

  const path = new Path2D(d);
  pathCache.set(d, path);
  return path;
};

export interface CanvasIconOptions {
  /** Center of the icon, in canvas pixels. */
  centerX: number;
  centerY: number;
  size: number;
  color: string;
  alpha?: number;
}

/** Three shorter strokes. A 1.5-unit stroke goes hazy when scaled down. */
export const drawFilterLinesMark = (
  ctx: CanvasRenderingContext2D,
  {
    centerX,
    centerY,
    width,
    color,
    alpha,
  }: {
    centerX: number;
    centerY: number;
    width: number;
    color: string;
    alpha?: number;
  },
): void => {
  const thickness = 2;
  const gap = thickness + 1.8;
  const widths = [width, width * 0.68, width * 0.36];
  const left = centerX - width / 2;
  const top = centerY - gap;

  ctx.save();
  if (alpha != null) ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;
  ctx.lineCap = "round";

  widths.forEach((lineWidth, index) => {
    // An even-width stroke stays crisp when its center is on an integer pixel.
    const y = Math.round(top + index * gap);
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(left + lineWidth, y);
    ctx.stroke();
  });

  ctx.restore();
};

export const drawCanvasIcon = (
  ctx: CanvasRenderingContext2D,
  icon: CanvasIconName,
  { centerX, centerY, size, color, alpha }: CanvasIconOptions,
): void => {
  const scale = size / ICON_VIEWBOX;

  ctx.save();
  if (alpha != null) ctx.globalAlpha = alpha;
  ctx.translate(centerX - size / 2, centerY - size / 2);
  ctx.scale(scale, scale);
  ctx.fillStyle = color;
  ctx.fill(iconPath(CANVAS_ICON[icon]), "evenodd");
  ctx.restore();
};
